import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useAtualizarEnderecoMutation,
  useAtualizarPerfilMutation,
  useCriarEnderecoMutation,
  useGetEnderecosQuery,
  useGetMeQuery,
  useRemoverEnderecoMutation,
  type Endereco,
} from '../../api';
import type { RootState } from '../../store';
import { limparTokens } from '../../store/auth-slice';
import { CabecalhoModal } from '../../components/cabecalho-modal';
import { DialogLogin } from '../../components/dialog-login';
import { FormEndereco, metaTipoEndereco, type EnderecoPreenchido } from '../../components/form-endereco';
import { urlAvatarUsuario } from '../../lib/avatar';
import { CartaoCashback } from './cartao-cashback'

export function ContaPage(): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Com itens no carrinho, o "continuar" leva ao checkout; sem itens, ao cardápio.
  const itensCarrinho = useSelector((s: RootState) => s.carrinho.itens);
  const temCarrinho = itensCarrinho.length > 0;

  const theme = useTheme();
  // Desktop: a conta abre como modal centralizado; mobile: tela cheia.
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const { data: usuario, isLoading: carregandoUsuario, error: erroUsuario } = useGetMeQuery(undefined, {
    skip: !accessToken,
  });
  const { data: enderecos, isLoading: carregandoEnderecos } = useGetEnderecosQuery(undefined, {
    skip: !accessToken,
  });
  const [criarEndereco, statusCriar] = useCriarEnderecoMutation();
  const [atualizarEndereco, statusAtualizar] = useAtualizarEnderecoMutation();
  const [removerEndereco] = useRemoverEnderecoMutation();
  const [atualizarPerfil, statusPerfil] = useAtualizarPerfilMutation();

  // Edição dos dados pessoais (nome/CPF). O e-mail é a identidade da conta e não é editável aqui.
  const [editandoPerfil, setEditandoPerfil] = useState(false);
  const [formPerfil, setFormPerfil] = useState<{ nome: string; cpf: string; telefone: string }>({
    nome: '',
    cpf: '',
    telefone: '',
  });

  const [idSelecionado, setIdSelecionado] = useState<number | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  // Id do endereço em edição (null = novo endereço).
  const [idEditando, setIdEditando] = useState<number | null>(null);
  const [enderecoEmEdicao, setEnderecoEmEdicao] = useState<Partial<EnderecoPreenchido> | undefined>(undefined);
  const [erro, setErro] = useState<string | null>(null);

  // Seleção inicial: o endereço principal (ou o primeiro da lista).
  useEffect(() => {
    if (!enderecos || enderecos.length === 0) {
      setIdSelecionado(null);
      return;
    }
    const aindaExiste = enderecos.some((e) => e.id === idSelecionado);
    if (!aindaExiste) {
      const principal = enderecos.find((e) => e.principal) ?? enderecos[0]!;
      setIdSelecionado(principal.id);
    }
  }, [enderecos, idSelecionado]);

  function voltar(): void {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/catalogo');
    }
  }

  // Envolve o conteúdo no modal (desktop) ou tela cheia (mobile).
  const comoModal = (corpo: JSX.Element): JSX.Element => (
    <Dialog
      open
      onClose={voltar}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ sx: { position: 'relative', ...(ehDesktop ? { maxHeight: '90vh' } : {}) } }}
    >
      <CabecalhoModal titulo="Minha conta" aoFechar={voltar} />
      <DialogContent sx={{ pt: 2, pb: 6 }}>{corpo}</DialogContent>
    </Dialog>
  );

  // Sem token (ou sessão expirada): abre o login no lugar. Ao concluir, o token
  // entra na store e esta tela reexibe a conta (as queries saem do skip). Fechar
  // sem logar volta pra tela anterior.
  if (!accessToken || erroUsuario) {
    return <DialogLogin aberto onFechar={voltar} onSucesso={() => undefined} />;
  }

  if (carregandoUsuario || !usuario) {
    return comoModal(
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>,
    );
  }

  const salvando = statusCriar.isLoading || statusAtualizar.isLoading;

  function abrirEdicaoPerfil(): void {
    if (!usuario) return;
    setErro(null);
    setFormPerfil({ nome: usuario.nome, cpf: usuario.cpf ?? '', telefone: usuario.telefone ?? '' });
    setEditandoPerfil(true);
  }

  async function salvarPerfil(): Promise<void> {
    setErro(null);
    try {
      await atualizarPerfil({
        nome: formPerfil.nome.trim(),
        // CPF e telefone são opcionais: vazio limpa o campo (null).
        cpf: formPerfil.cpf.trim() || null,
        telefone: formPerfil.telefone.trim() || null,
      }).unwrap();
      setEditandoPerfil(false);
    } catch (err) {
      setErro(extrairMsg(err) ?? 'Não foi possível salvar seus dados');
    }
  }

  /** Abre o formulário em branco para cadastrar um novo endereço. */
  function abrirNovoEndereco(): void {
    setErro(null);
    setIdEditando(null);
    setEnderecoEmEdicao(undefined);
    setMostrarForm(true);
  }

  /** Abre o formulário já preenchido com os dados de um endereço para edição. */
  function abrirEdicaoEndereco(e: Endereco): void {
    setErro(null);
    setEnderecoEmEdicao({
      tipo: metaTipoEndereco(e.tipo).valor,
      logradouro: e.logradouro,
      numero: e.numero ?? '',
      cep: e.cep ?? '',
      bairro: e.bairro,
      complemento: e.complemento ?? '',
      cidade: e.cidade,
      uf: e.uf,
      latitude: e.latitude ? Number(e.latitude) : undefined,
      longitude: e.longitude ? Number(e.longitude) : undefined,
    });
    setIdEditando(e.id);
    setMostrarForm(true);
  }

  function fecharForm(): void {
    setMostrarForm(false);
    setIdEditando(null);
    setEnderecoEmEdicao(undefined);
  }

  async function salvarEndereco(dados: EnderecoPreenchido): Promise<void> {
    setErro(null);
    try {
      if (idEditando != null) {
        await atualizarEndereco({ id: idEditando, dados }).unwrap();
        setIdSelecionado(idEditando);
      } else {
        const criado = await criarEndereco(dados).unwrap();
        // Recém-cadastrado já fica selecionado para o "continuar".
        setIdSelecionado(criado.id);
      }
      fecharForm();
    } catch (err) {
      setErro(extrairMsg(err) ?? 'Não foi possível salvar o endereço');
    }
  }

  async function excluirEndereco(id: number): Promise<void> {
    setErro(null);
    try {
      await removerEndereco(id).unwrap();
      if (idSelecionado === id) setIdSelecionado(null);
    } catch (err) {
      setErro(extrairMsg(err) ?? 'Não foi possível remover o endereço');
    }
  }

  /**
   * Marca o endereço escolhido como principal (o checkout pré-seleciona o
   * principal) e segue: com itens no carrinho vai ao checkout; sem itens,
   * ao cardápio.
   */
  async function continuarComEndereco(): Promise<void> {
    setErro(null);
    const selecionado = enderecos?.find((e) => e.id === idSelecionado);
    if (!selecionado) return;
    try {
      if (!selecionado.principal) {
        await atualizarEndereco({ id: selecionado.id, dados: { principal: true } }).unwrap();
      }
      navigate(temCarrinho ? '/checkout' : '/catalogo');
    } catch (err) {
      setErro(extrairMsg(err) ?? 'Não foi possível selecionar o endereço');
    }
  }

  return comoModal(
    <div>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        )}

        {editandoPerfil ? (
          <Stack spacing={2}>
            <TextField
              label="Nome"
              value={formPerfil.nome}
              onChange={(e) => setFormPerfil((f) => ({ ...f, nome: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Telefone (opcional)"
              type="tel"
              value={formPerfil.telefone}
              onChange={(e) => setFormPerfil((f) => ({ ...f, telefone: e.target.value }))}
              fullWidth
              autoComplete="tel"
              inputProps={{ inputMode: 'tel' }}
              helperText="Usado pelo restaurante para falar com você sobre o pedido."
            />
            <TextField
              label="CPF (opcional)"
              value={formPerfil.cpf}
              onChange={(e) => setFormPerfil((f) => ({ ...f, cpf: e.target.value }))}
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
            />
            {/* O e-mail é a identidade da conta (login por código): não editável aqui. */}
            <TextField
              label="E-mail"
              value={usuario.email}
              fullWidth
              disabled
              helperText="O e-mail é usado para acessar sua conta e não pode ser alterado."
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={salvarPerfil}
                disabled={!formPerfil.nome.trim() || statusPerfil.isLoading}
              >
                {statusPerfil.isLoading ? <CircularProgress size={20} /> : 'Salvar dados'}
              </Button>
              <Button onClick={() => setEditandoPerfil(false)} disabled={statusPerfil.isLoading}>
                Cancelar
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box>
            {/* Cabeçalho: avatar + nome em destaque, com ação de editar à direita. */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={urlAvatarUsuario(usuario.email)}
                sx={{ width: 56, height: 56, bgcolor: 'grey.100', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="h6" noWrap>
                  {usuario.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Dados da conta
                </Typography>
              </Box>
              <Button startIcon={<EditIcon />} onClick={abrirEdicaoPerfil} sx={{ flexShrink: 0 }}>
                Editar
              </Button>
            </Stack>

            {/* Detalhes em linhas com ícone + rótulo + valor, num painel sutil. */}
            <Stack spacing={1.5} sx={{ mt: 2.5, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                    E-mail
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {usuario.email}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PhoneOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                    Telefone
                  </Typography>
                  {usuario.telefone ? (
                    <Typography variant="body2">{usuario.telefone}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled" fontStyle="italic">
                      Não informado
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <BadgeOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                    CPF
                  </Typography>
                  {usuario.cpf ? (
                    <Typography variant="body2">{usuario.cpf}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled" fontStyle="italic">
                      Não informado
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <CartaoCashback usuario={usuario} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">Endereços</Typography>
          {!mostrarForm && (
            <Button startIcon={<AddIcon />} onClick={abrirNovoEndereco}>
              Novo endereço
            </Button>
          )}
        </Stack>

        {(enderecos ?? []).length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Selecione o endereço de entrega e toque em continuar.
          </Typography>
        )}

        {carregandoEnderecos ? (
          <CircularProgress size={20} />
        ) : (
          <List>
            {(enderecos ?? []).map((e) => {
              const { Icone, rotulo } = metaTipoEndereco(e.tipo);
              return (
                <ListItemButton
                  key={e.id}
                  divider
                  selected={idSelecionado === e.id}
                  onClick={() => setIdSelecionado(e.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Radio edge="start" checked={idSelecionado === e.id} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>
                    <Icone fontSize="small" />
                  </Box>
                  <ListItemText
                    primary={`${rotulo}${e.principal ? ' (principal)' : ''}`}
                    secondary={`${e.logradouro}, ${e.numero ?? 's/n'} — ${e.bairro}, ${e.cidade}/${e.uf}`}
                  />
                  <IconButton
                    edge="end"
                    aria-label="Editar endereço"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      abrirEdicaoEndereco(e);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="error"
                    aria-label="Remover endereço"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      void excluirEndereco(e.id);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </ListItemButton>
              );
            })}
            {(enderecos ?? []).length === 0 && !mostrarForm && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                Nenhum endereço cadastrado ainda.
              </Typography>
            )}
          </List>
        )}

        {mostrarForm && (
          <FormEndereco
            key={idEditando ?? 'novo'}
            inicial={enderecoEmEdicao}
            titulo={idEditando != null ? 'Editar endereço' : 'Em qual local devemos levar?'}
            textoSalvar={idEditando != null ? 'Salvar alterações' : 'Salvar endereço'}
            salvando={salvando}
            onSalvar={(dados) => void salvarEndereco(dados)}
            onCancelar={fecharForm}
          />
        )}

        {/* Ação principal: confirmar o endereço escolhido e seguir o fluxo. Some
            enquanto o formulário está aberto — ali quem manda é "Salvar
            endereço", e um segundo botão grande logo abaixo só confunde. */}
        {(enderecos ?? []).length > 0 && !mostrarForm && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={continuarComEndereco}
            disabled={!idSelecionado || statusAtualizar.isLoading}
            sx={{ mt: 2 }}
          >
            {statusAtualizar.isLoading ? <CircularProgress size={20} /> : temCarrinho ? 'Continuar' : 'Ver o cardápio'}
          </Button>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            size="small"
            color="inherit"
            endIcon={<LogoutIcon/>}
            sx={{ color: 'text.secondary' }}
            onClick={() => dispatch(limparTokens())}
          >
            Realizar logout
          </Button>
        </Stack>

    </div>,
  );
}

function extrairMsg(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { mensagem?: string } }).data;
    return d?.mensagem ?? null;
  }
  return null;
}
